import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useWatch, useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { CopyIcon as DuplicateIcon } from 'lucide-react'
import { InputField } from 'components/InputField'
import Dropdown from 'components/Dropdown'
import Button from 'components/Button'
import { DropDownItem } from 'types/components'
import {
  VoucherViewFormSchemas,
  VoucherViewFormTypes,
} from 'schemas/campaign-voucher-schemas'
import {
  useVoucherMerchantsSelectQuery,
} from './use-voucher-merchants-lookup'
import { postGetVouchersAPI } from 'api/campaign-voucher'
import { useVouchersStore } from 'store/vouchers'

import { VOUCHER_TYPES_DROPDOWN, MERCHANT_SELECTION_TYPES_DROPDOWN } from 'constants/filters'

export const ViewVoucherForm = ({
  voucherId,
  onCloseModal,
}: {
  voucherId: number
  onCloseModal: () => void
}) => {
  const navigate = useNavigate()
  const { selectedMerchants, onSelectMerchants } =
    useVoucherMerchantsSelectQuery()
  const clonedVoucherDataSet = useVouchersStore(
    (state) => state.clonedVoucherDataSet
  )

  const [loading, setLoading] = useState(true)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [voucherData, setVoucherData] = useState<any>(null)

  useEffect(() => {
    async function fetchVoucher() {
      setLoading(true)
      try {
        const response = await postGetVouchersAPI({
          voucherId,
        })
        const voucher = response.data?.[0]
        if (voucher) {
          setVoucherData(voucher)
          const merchantTypeId =
            voucher.merchants?.length === 1 && voucher.merchants[0] === 'All stores'
              ? 1
              : 2
          form.reset({
            mca: voucher.mcaId + ' ' + voucher.mcaName,
            voucherId: voucher.voucherId,
            voucherCode: voucher.voucherCode,
            voucherTypeId: voucher.voucherTypeId,
            voucherValue: voucher.voucherValue,
            voucherName: voucher.voucherName ?? '',
            voucherIssuer: voucher.voucherIssuerId + ' ' + voucher.voucherIssuerName,
            startDate: voucher.startDate?.split('T')[0] ?? '',
            endDate: voucher.endDate?.split('T')[0] ?? '',
            description: voucher.description ?? '',
            imageUrl: voucher.imageUrl ?? '',
            offerValue2: voucher.offerValue2 ?? 0,
            offerValue3: voucher.offerValue3 ?? 0,
            offerTypeId: voucher.offerTypeId ?? undefined,
            pluCode: voucher.pluCode ?? '',
            merchants: voucher.merchants,
            productSkus: voucher.productSkus,
            merchantTypeId,
          })
          if (merchantTypeId === 2 && voucher.merchants?.length) {
            const mapped = voucher.merchants.map((m: string) => {
              const match = m.match(/\((\d+)\)$/)
              const id = match ? match[1] : m
              return { label: m, value: id }
            })

            onSelectMerchants(mapped)
            form.setValue('merchants', mapped.map(m => m.value))
          }
        }
      } finally {
        setLoading(false)
      }
    }
    fetchVoucher()
    // eslint-disable-next-line
  }, [voucherId])

  const handleDuplicate = useCallback(() => {
    if (voucherData) {
      clonedVoucherDataSet(voucherData)
      onCloseModal()
      // Trigger the create-voucher modal by navigating with query param
      navigate('?modal=create-voucher')
    }
  }, [voucherData, clonedVoucherDataSet, onCloseModal, navigate])


  const form = useForm<VoucherViewFormTypes>({
    resolver: zodResolver(VoucherViewFormSchemas)
  })

  const merchantTypeId = useWatch({
    control: form.control,
    name: 'merchantTypeId',
  })

  useEffect(() => {
    if (merchantTypeId === 1) {
      // “All stores”
      form.setValue('merchants', ['All stores'])
    } else if (merchantTypeId === 2) {
      // “Specific stores” — remove “All stores” if it exists
      const current = form.getValues('merchants')
      if (Array.isArray(current)) {
        form.setValue(
          'merchants',
          current.filter((m) => m !== 'All stores')
        )
      }
    }
  }, [merchantTypeId, form])

  if (loading) return <div className="p-4">Loading...</div>

  return (
    <form title="View Voucher" className="space-y-8">
      <div className="mb-6 flex gap-2">
        <Button
          type="button"
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
          onClick={handleDuplicate}
        >
          <DuplicateIcon className="w-4 h-4 inline mr-2" />
          Duplicate
        </Button>
      </div>

      <div className="mb-4">
        <Controller
          control={form.control}
          name="mca"
          render={({ field: { value } }) => (
            <InputField
              type="input"
              name="mca"
              label="MCA"
              containerClassName="w-full"
              value={value ?? ""}
              disable
            />
          )}
        />
      </div>

      <div className="mb-4 flex gap-4">
        <Controller
          control={form.control}
          name="voucherId"
          render={({ field: { value } }) => (
            <InputField
              type="input"
              name="voucherId"
              label="Voucher ID"
              containerClassName="flex-1"
              value={value ?? ""}
              disable
            />
          )}
        />
        <Controller
          control={form.control}
          name="voucherCode"
          render={({ field: { value } }) => (
            <InputField
              type="input"
              name="voucherCode"
              label="Voucher Code"
              containerClassName="w-full"
              value={value ?? ""}
              disable
            />
          )}
        />
      </div>


      <div className="mb-4">
        <Controller
          control={form.control}
          name="voucherTypeId"
          render={({ field: { onChange, value } }) => (
            <Dropdown
              defaultValue={VOUCHER_TYPES_DROPDOWN.find(
                (i) => i.value === String(value)
              )}
              title="Voucher Type"
              options={VOUCHER_TYPES_DROPDOWN}
              onSelect={(option: DropDownItem) => {
                onChange(Number(option.value))
              }}
              className="w-full"
              disabled
            />
          )}
        />
      </div>

      {/* Show this field only when merchant type 1 is selected */}
      {form.watch("merchantTypeId") === 1 && (
        <div className="mb-4">
          <Controller
            control={form.control}
            name="merchantTypeId"
            render={({ field: { onChange, value } }) => (
              <Dropdown
                value={MERCHANT_SELECTION_TYPES_DROPDOWN.find(
                  (i) => i.value === String(value)
                )}
                title="Stores to use the voucher"
                options={MERCHANT_SELECTION_TYPES_DROPDOWN}
                onSelect={(option: DropDownItem) => {
                  onChange(Number(option.value))
                }}
                className="w-full"
                disabled
              />
            )}
          />
        </div>
      )}

      {/* Show Merchant Lookup only when merchant type 2 is selected */}
      {form.watch("merchantTypeId") === 2 && (
        <>
          <div className="mb-4 items-center mt-2">
            <Controller
              name="merchants"
              control={form.control}
              render={({ field }) => (
                <>
                  {/* Selected merchants */}
                  {selectedMerchants.length > 0 && (
                    <div className="mb-4">
                      <p className="font-light tracking-wide">Selected Stores</p>
                      <div className="flex gap-2 mt-2 flex-wrap max-w-lg">
                        {selectedMerchants.map((item) => (
                          <span
                            key={item.value}
                            className="cursor-pointer flex items-center justify-between gap-2 bg-gray-100 px-2 py-1 tracking-wide rounded-md"
                          >
                            {item.label}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            />
          </div>
        </>
      )}

      <div className="mb-4">
        <Controller
          control={form.control}
          name="voucherName"
          render={({ field: { value } }) => (
            <InputField
              type="input"
              name="voucherName"
              label="Voucher Name"
              containerClassName="w-full"
              value={value ?? ""}
              disable
            />
          )}
        />
      </div>
      <div className="mb-4">
        <Controller
          control={form.control}
          name="voucherIssuer"
          render={({ field: { value } }) => (
            <InputField
              type="input"
              name="voucherIssuer"
              label="Voucher Issuer"
              containerClassName="w-full"
              value={value ?? ""}
              disable
            />
          )}
        />
      </div>
      <div className="mb-4">
        <Controller
          control={form.control}
          name="description"
          render={({ field: { value } }) => (
            <InputField
              type="input"
              name="description"
              label="Description"
              containerClassName="w-full"
              value={value ?? ""}
              disable
            />
          )}
        />
      </div>
      <div className="mb-4">
        <Controller
          control={form.control}
          name="imageUrl"
          render={({ field: { value } }) => (
            <InputField
              type="input"
              name="imageUrl"
              label="Image URL"
              containerClassName="w-full"
              value={value ?? ""}
              disable
            />
          )}
        />
      </div>
      <div className="mb-4 flex gap-4">
        <Controller
          control={form.control}
          name="offerTypeId"
          render={({ field: { value } }) => (
            <InputField
              type="input"
              name="offerTypeId"
              label="Offer Type ID"
              containerClassName="flex-1"
              value={value ?? ""}
              disable
            />
          )}
        />

        <Controller
          control={form.control}
          name="offerValue2"
          render={({ field: { value } }) => (
            <InputField
              type="input"
              name="offerValue2"
              label="Min Spend Amount"
              containerClassName="flex-1"
              value={value ?? ""}
              disable
            />
          )}
        />

        <Controller
          control={form.control}
          name="offerValue3"
          render={({ field: { value } }) => (
            <InputField
              type="input"
              name="offerValue3"
              label="Offer Value"
              containerClassName="flex-1"
              value={value ?? ""}
              disable
            />
          )}
        />
      </div>
      <div className="mb-4 flex gap-4">
        <Controller
          control={form.control}
          name="voucherValue"
          render={({ field: { value } }) => (
            <InputField
              type="input"
              name="voucherValue"
              label="Voucher Value"
              containerClassName="flex-1"
              value={value ?? ""}
              disable
            />
          )}
        />
        <Controller
          control={form.control}
          name="pluCode"
          render={({ field: { value } }) => (
            <InputField
              type="input"
              name="pluCode"
              label="PLU Code"
              containerClassName="w-full"
              value={value ?? ""}
              disable
            />
          )}
        />
      </div>
      <div className="mb-4">
        <Controller
          control={form.control}
          name="productSkus"
          render={({ field: { value } }) => (
            <InputField
              type="input"
              name="productSkus"
              label="Product SKUs"
              containerClassName="w-full"
              value={(value || []).join(", ")}
              disable
            />
          )}
        />
      </div>
      <div className="mb-4">
        <Controller
          control={form.control}
          name="startDate"
          render={({ field: { value } }) => (
            <InputField
              type="date"
              name="startDate"
              label="Start Date"
              containerClassName="w-full"
              value={value ?? ""}
              disable
            />
          )}
        />
      </div>
      <div className="mb-4">
        <Controller
          control={form.control}
          name="endDate"
          render={({ field: { value } }) => (
            <InputField
              type="date"
              name="endDate"
              label="End Date"
              containerClassName="w-full"
              value={value ?? ""}
              disable
            />
          )}
        />
      </div>
    </form >
  )
}
